import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import client, { apiError } from "@/lib/api";

function Section({ title, description, children }) {
    return (
        <div className="grid gap-6 border-t border-line py-8 lg:grid-cols-[280px_1fr]">
            <div>
                <h2 className="text-[17px] font-bold tracking-tight text-ink">{title}</h2>
                <p className="mt-1 text-[14px] leading-[1.6] text-subtle">{description}</p>
            </div>
            <div>{children}</div>
        </div>
    );
}

export default function Ajustes() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";

    const [name, setName] = useState(user?.name || "");
    const [pw, setPw] = useState({ current_password: "", new_password: "" });
    const [settings, setSettings] = useState(null);

    useEffect(() => { setName(user?.name || ""); }, [user]);
    useEffect(() => {
        if (isAdmin) client.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    }, [isAdmin]);

    const saveProfile = async (e) => {
        e.preventDefault();
        try {
            await client.patch("/account/profile", { name });
            await refreshUser();
            toast.success("Perfil actualizado.");
        } catch (err) { toast.error(apiError(err)); }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        try {
            await client.post("/auth/change-password", pw);
            setPw({ current_password: "", new_password: "" });
            toast.success("Contraseña actualizada.");
        } catch (err) { toast.error(apiError(err)); }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            const { data } = await client.put("/admin/settings", settings);
            setSettings(data);
            toast.success("Datos del negocio guardados.");
        } catch (err) { toast.error(apiError(err)); }
    };

    const deleteAccount = async () => {
        try {
            await client.post("/account/delete-request");
            toast.success("Cuenta eliminada. Sentimos verte marchar.");
            await logout();
            navigate("/");
        } catch (err) { toast.error(apiError(err)); }
    };

    const sf = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

    return (
        <div className="mx-auto max-w-4xl">
            <PageHeader title="Ajustes" subtitle="Tu perfil, tu contraseña y los datos de tu cuenta." />

            <Section title="Tu perfil" description="El nombre con el que te saludamos.">
                <form onSubmit={saveProfile} className="max-w-md space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Nombre</Label>
                        <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
                    </div>
                    <div className="space-y-2">
                        <Label>Correo</Label>
                        <Input value={user?.email || ""} disabled className="text-subtle" />
                    </div>
                    <Button type="submit" data-testid="settings-save-profile">Guardar perfil</Button>
                </form>
            </Section>

            <Section title="Contraseña" description="Cámbiala cuando quieras. Usa letras y números.">
                <form onSubmit={savePassword} className="max-w-md space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="cpw">Contraseña actual</Label>
                        <Input id="cpw" type="password" value={pw.current_password}
                            onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-pw" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="npw">Nueva contraseña</Label>
                        <Input id="npw" type="password" value={pw.new_password}
                            onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-pw" />
                    </div>
                    <Button type="submit" data-testid="settings-save-pw">Cambiar contraseña</Button>
                </form>
            </Section>

            {isAdmin && settings && (
                <Section title="Datos del negocio" description="Textos y precios que puedes cambiar sin tocar el código.">
                    <form onSubmit={saveSettings} className="max-w-lg space-y-4">
                        <div className="space-y-2">
                            <Label>Frase de la marca</Label>
                            <Input value={settings.tagline} onChange={sf("tagline")} data-testid="settings-tagline" />
                        </div>
                        <div className="space-y-2">
                            <Label>Correo de contacto (pie y correos)</Label>
                            <Input value={settings.contact_email} onChange={sf("contact_email")}
                                placeholder="Aún no disponible" data-testid="settings-contact" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nombre plan gratuito</Label>
                                <Input value={settings.plan_free_name} onChange={sf("plan_free_name")} />
                            </div>
                            <div className="space-y-2">
                                <Label>Precio plan gratuito</Label>
                                <Input value={settings.plan_free_price} onChange={sf("plan_free_price")} />
                            </div>
                            <div className="space-y-2">
                                <Label>Nombre plan de pago</Label>
                                <Input value={settings.plan_premium_name} onChange={sf("plan_premium_name")} />
                            </div>
                            <div className="space-y-2">
                                <Label>Precio plan de pago (etiqueta)</Label>
                                <Input value={settings.plan_premium_price} onChange={sf("plan_premium_price")} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Importe del pago en céntimos (1099 = 10,99 €)</Label>
                            <Input type="number" value={settings.price_amount} onChange={sf("price_amount")}
                                data-testid="settings-price" className="max-w-[200px]" />
                            <p className="text-[12px] text-subtle">Es lo que se cobra en Stripe. Cambia también la etiqueta de arriba para que coincida.</p>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Límite análisis</Label>
                                <Input type="number" value={settings.limit_analyses} onChange={sf("limit_analyses")} />
                            </div>
                            <div className="space-y-2">
                                <Label>Límite productos</Label>
                                <Input type="number" value={settings.limit_products} onChange={sf("limit_products")} />
                            </div>
                            <div className="space-y-2">
                                <Label>Límite informes</Label>
                                <Input type="number" value={settings.limit_reports} onChange={sf("limit_reports")} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Recordar análisis cada (días)</Label>
                            <Input type="number" value={settings.reminder_days} onChange={sf("reminder_days")} className="max-w-[160px]" />
                        </div>
                        <Button type="submit" data-testid="settings-save-business">Guardar cambios</Button>
                    </form>
                </Section>
            )}

            <Section title="Eliminar mi cuenta" description="Borra tu cuenta y todos tus datos. No se puede deshacer.">
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/5"
                            data-testid="delete-account-open">
                            Eliminar mi cuenta
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>¿Seguro que quieres eliminar tu cuenta?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Borraremos tu perfil, tus análisis, tu neceser y tus informes. Esta acción no
                                tiene vuelta atrás.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={deleteAccount} data-testid="delete-account-confirm"
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Sí, eliminar mi cuenta
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </Section>
        </div>
    );
}
